import React, {useRef, useState} from 'react';
import {Button} from '@mui/material';
import UserAvatar from '../UserAvatar/UserAvatar';
import {getCurrentUser, getCurrentUserId} from '../../lib/picos/currentLogin';
import {getContactFromUserId} from '../../lib/picos/getContactFromUser';
import {getUserInitials} from '../../Contacts/ContactFormat';
import {saveContactQueue} from '../../store/actions/contactActions';
import {saveUser} from '../../store/actions/userActions';
import Contact from '../../entities/contact';
import User from '../../entities/user';
import Auth from '../../Auth/Auth';
import IDP_CONFIG from '../../Auth/idp-config';
import SignInSecuritySection from './SignInSecuritySection';
import store from '../../store/store';
import {uploadAvatar, getAvatarUrl} from '../../rest/api';
import {confirmError} from '../Confirm';
import {isDemo} from '../../lib/demo';
import ValidatedInput, {ValidatedInputRef} from '../ValidatedInput/ValidatedInput';
import './MyProfileDialog.less';

type MyProfileDialogProps = {
    onClose?: () => void;
};

function resizeImage(dataUrl: string, maxSize: number): Promise<string> {
    return new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => {
            let {width, height} = img;
            if (width > maxSize || height > maxSize) {
                if (width > height) {
                    height = Math.round(height * maxSize / width);
                    width = maxSize;
                } else {
                    width = Math.round(width * maxSize / height);
                    height = maxSize;
                }
            }
            const canvas = document.createElement('canvas');
            canvas.width = width;
            canvas.height = height;
            const ctx = canvas.getContext('2d');
            ctx.drawImage(img, 0, 0, width, height);
            resolve(canvas.toDataURL('image/jpeg', 0.85));
        };
        img.onerror = reject;
        img.src = dataUrl;
    });
}

export default function MyProfileDialog({onClose}: MyProfileDialogProps) {
    const userId = getCurrentUserId();
    const user = getCurrentUser();
    const contact = getContactFromUserId(userId);
    const demo = isDemo();

    const [first, setFirst] = useState(contact?.first || '');
    const [middle, setMiddle] = useState(contact?.middle || '');
    const [last, setLast] = useState(contact?.last || '');
    const [picturePreview, setPicturePreview] = useState<string | null>(
        getAvatarUrl(userId, user?.picture) || null
    );
    const [pendingImageDataUrl, setPendingImageDataUrl] = useState<string | null>(null);
    const [nameChanged, setNameChanged] = useState(false);
    const [saving, setSaving] = useState(false);

    // Email change state
    const [isEmailUpdating, setIsEmailUpdating] = useState(false);
    const [isEmailSubmitting, setIsEmailSubmitting] = useState(false);
    const [newEmail, setNewEmail] = useState<string | null>(null);
    const [isValidEmail, setIsValidEmail] = useState(false);
    const emailRef = useRef<ValidatedInputRef>(null);

    const fileInputRef = useRef<HTMLInputElement>(null);

    const initials = getUserInitials(userId, 2);
    const hasChanged = nameChanged || !!pendingImageDataUrl;

    const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onload = async () => {
            const resized = await resizeImage(reader.result as string, 200);
            setPicturePreview(resized);
            setPendingImageDataUrl(resized);
        };
        reader.readAsDataURL(file);
    };

    const handleChangePassword = () => {
        Auth.changePassword({
            email: user.email,
            connection: 'Username-Password-Authentication'
        });
    };

    const handleSave = async () => {
        setSaving(true);

        if (nameChanged) {
            const contactClone = new Contact(contact || {});
            contactClone.first = first;
            contactClone.middle = middle;
            contactClone.last = last;
            store.dispatch(saveContactQueue(contactClone));
        }

        if (pendingImageDataUrl) {
            try {
                await uploadAvatar(pendingImageDataUrl);
            }
            catch (err) {
                console.error('MyProfileDialog: avatar upload failed', err);
                confirmError('Upload Failed', 'Could not upload profile picture. Please try again.');
            }
        }

        onClose?.();
    };

    const handleFieldChange = (setter: (v: string) => void) => (e: React.ChangeEvent<HTMLInputElement>) => {
        setter(e.target.value);
        setNameChanged(true);
    };

    // Email change handlers
    const onEmailChange = (_value: string, valid: boolean) => {
        if (valid) setIsValidEmail(true);
        else setIsValidEmail(false);
    };

    const onValidEmail = (email: string) => setNewEmail(email);
    const onInvalidEmail = () => setNewEmail(null);

    const startEmailUpdate = () => {
        setIsEmailUpdating(true);
        emailRef.current?.focus();
    };

    const endEmailUpdate = () => {
        emailRef.current?.clear();
        setIsEmailSubmitting(true);

        Auth.changeEmail(user.id, user.email, newEmail, (updatedUser: User) => {
            setIsEmailSubmitting(false);
            if (updatedUser) {
                updatedUser.persisted = true;
                store.dispatch(saveUser(updatedUser));
                setIsEmailUpdating(false);
            }
        });
    };

    const users = store.getState().users;
    const isConnected = store.getState().misc.isConnected;

    return (
        <div className="my-profile-dialog" style={isEmailSubmitting ? {cursor: 'wait'} : undefined}>
            {/* Avatar + Name Fields */}
            <div className="my-profile-dialog__avatar-section">
                <div className="my-profile-dialog__avatar-wrapper" onClick={() => fileInputRef.current?.click()}>
                    <UserAvatar initials={initials} picture={picturePreview} size={80} />
                    <div className="my-profile-dialog__avatar-overlay">Change</div>
                </div>
                <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    style={{display: 'none'}}
                    onChange={handleFileSelect}
                />
                <span className="my-profile-dialog__avatar-hint">Click to change picture</span>
            </div>

            <div className="my-profile-dialog__fields">
                <label className="my-profile-dialog__label">
                    First Name
                    <input type="text" value={first} onChange={handleFieldChange(setFirst)} className="my-profile-dialog__input" />
                </label>
                <label className="my-profile-dialog__label">
                    Middle Name
                    <input type="text" value={middle} onChange={handleFieldChange(setMiddle)} className="my-profile-dialog__input" />
                </label>
                <label className="my-profile-dialog__label">
                    Last Name
                    <input type="text" value={last} onChange={handleFieldChange(setLast)} className="my-profile-dialog__input" />
                </label>
            </div>

            <div className="my-profile-dialog__actions">
                <Button variant="text" size="small" onClick={onClose} disabled={saving}>
                    Cancel
                </Button>
                <Button variant="contained" size="small" onClick={handleSave} disabled={!hasChanged || saving}>
                    {saving ? 'Saving...' : 'Save'}
                </Button>
            </div>

            {/* Account Section */}
            <div className="my-profile-dialog__section-divider">
                <span>Account</span>
            </div>

            <div className="my-profile-dialog__account-section">
                <div className="my-profile-dialog__email-row">
                    <span className="my-profile-dialog__email-label">Email</span>
                    <ValidatedInput
                        type="email"
                        autoFocus={isEmailUpdating}
                        ref={emailRef}
                        blacklist={users.map((u: User) => u.email)}
                        blacklistedMessage="This email is already in use"
                        invalidValueMessage="Invalid email address"
                        onChange={onEmailChange}
                        onValid={onValidEmail}
                        onInvalid={onInvalidEmail}
                        showErrorMessage={false}
                        showIcons={false}
                        defaultValue={user?.email}
                        disabled={!isEmailUpdating}
                    />
                </div>

                <div className="my-profile-dialog__button-row">
                    {isEmailUpdating && (
                        <Button variant="outlined" size="small" color="warning" onClick={() => { setIsEmailUpdating(false); emailRef.current?.clear(); }}>
                            Cancel
                        </Button>
                    )}
                    <Button
                        variant="outlined"
                        size="small"
                        disabled={demo || !isConnected || (isEmailUpdating && !isValidEmail)}
                        onClick={isEmailUpdating ? endEmailUpdate : startEmailUpdate}
                    >
                        {isEmailUpdating ? 'Submit Change' : 'Change Email'}
                    </Button>
                    <Button variant="outlined" size="small" disabled={demo} onClick={handleChangePassword}>
                        Change Password
                    </Button>
                </div>
            </div>

            {/* Outlaw Sign-in owns passkeys, factors and devices; only Zitadel sign-in has it. */}
            {!demo && IDP_CONFIG.provider === 'zitadel' && <SignInSecuritySection />}
        </div>
    );
}
